import { t } from "@lingui/macro";
import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription, Button } from "@reactive-resume/ui";
import { OptimizationQuestionDto } from "@reactive-resume/dto";
import { useDialog } from "@/client/stores/dialog";
import {
  CircleNotch,
  Sparkle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Check,
  SkipForward,
} from "@phosphor-icons/react";

type QuestionAnswers = Record<string, string | string[]>;

export const OptimizationQuestionsDialog = () => {
  const { isOpen, payload, close } = useDialog<{
    questions: OptimizationQuestionDto[];
    onSubmit: (answers: QuestionAnswers) => void;
    isLoading?: boolean;
  }>("optimization-questions");

  const [answers, setAnswers] = useState<QuestionAnswers>({});
  const [currentStep, setCurrentStep] = useState(0);

  const questions = payload?.item?.questions || [];
  const onSubmit = payload?.item?.onSubmit;
  const isLoading = payload?.item?.isLoading || false;

  const currentQuestion = questions[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === questions.length - 1;
  const totalSteps = questions.length;

  const handleAnswerChange = (value: string) => {
    if (currentQuestion?.type === "radio") {
      // Handle multi-select for radio type questions
      setAnswers((prev) => {
        const currentAnswers = prev[currentStep];
        let updatedAnswers: string[];

        if (Array.isArray(currentAnswers)) {
          // Toggle the option in the array
          updatedAnswers = currentAnswers.includes(value)
            ? currentAnswers.filter((item) => item !== value)
            : [...currentAnswers, value];
        } else {
          // Convert single value to array or create new array
          updatedAnswers = currentAnswers === value ? [] : [value];
        }

        return {
          ...prev,
          [currentStep]: updatedAnswers,
        };
      });
    } else {
      // Handle single value for other question types (input, textarea)
      setAnswers((prev) => ({
        ...prev,
        [currentStep]: value,
      }));
    }
  };

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      const answeredQuestions = Object.fromEntries(
        Object.entries(answers)
          .filter(([_, answer]) => {
            if (Array.isArray(answer)) {
              return answer.length > 0;
            }
            return answer && answer.trim() !== "";
          })
          .map(([key, answer]) => {
            // Convert multi-select arrays to comma-separated strings
            if (Array.isArray(answer)) {
              return [key, answer.join(", ")];
            }
            return [key, answer];
          }),
      );
      onSubmit(answeredQuestions);
    }
    close();
    setCurrentStep(0);
    setAnswers({});
  };

  const handleSkip = () => {
    if (onSubmit) {
      const answeredQuestions = Object.fromEntries(
        Object.entries(answers)
          .filter(([_, answer]) => {
            if (Array.isArray(answer)) {
              return answer.length > 0;
            }
            return answer && answer.trim() !== "";
          })
          .map(([key, answer]) => {
            // Convert multi-select arrays to comma-separated strings
            if (Array.isArray(answer)) {
              return [key, answer.join(", ")];
            }
            return [key, answer];
          }),
      );
      onSubmit(answeredQuestions);
    }
    close();
    setCurrentStep(0);
    setAnswers({});
  };

  const handleClose = () => {
    close();
    setCurrentStep(0);
    setAnswers({});
  };

  const renderCurrentQuestion = () => {
    if (!currentQuestion) return null;

    const currentValue = answers[currentStep] || (currentQuestion.type === "radio" ? [] : "");

    switch (currentQuestion.type) {
      // case "input":
      //   return (
      //     <div className="space-y-6">
      //       <div className="space-y-3 text-center">
      //         <h3 className="text-foreground text-xl leading-tight font-semibold">
      //           {currentQuestion.question}
      //         </h3>
      //         {currentQuestion.metric && (
      //           <div className="from-primary/8 to-primary/12 border-primary/25 inline-flex items-center gap-2 rounded-full border bg-gradient-to-r px-3 py-1.5 shadow-sm">
      //             <Sparkle className="text-primary h-3 w-3" />
      //             <span className="text-primary text-xs font-medium">{currentQuestion.metric}</span>
      //           </div>
      //         )}
      //       </div>
      //       <div className="mx-auto max-w-md">
      //         <Input
      //           id="current-question"
      //           type="text"
      //           placeholder={currentQuestion.placeholder || "Type your answer..."}
      //           value={currentValue}
      //           onChange={(e) => handleAnswerChange(e.target.value)}
      //           className="focus:border-primary/50 border-2 px-4 py-6 text-center text-lg transition-all duration-200"
      //           autoFocus
      //         />
      //       </div>
      //     </div>
      //   );

      // case "textarea":
      //   return (
      //     <div className="space-y-6">
      //       <div className="space-y-3 text-center">
      //         <h3 className="text-foreground text-xl leading-tight font-semibold">
      //           {currentQuestion.question}
      //         </h3>
      //         {currentQuestion.metric && (
      //           <div className="from-primary/8 to-primary/12 border-primary/25 inline-flex items-center gap-2 rounded-full border bg-gradient-to-r px-3 py-1.5 shadow-sm">
      //             <Sparkle className="text-primary h-3 w-3" />
      //             <span className="text-primary text-xs font-medium">{currentQuestion.metric}</span>
      //           </div>
      //         )}
      //       </div>
      //       <div className="mx-auto max-w-lg">
      //         <textarea
      //           id="current-question"
      //           placeholder={currentQuestion.placeholder || "Share your thoughts..."}
      //           value={currentValue}
      //           onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
      //             handleAnswerChange(e.target.value)
      //           }
      //           rows={5}
      //           className="border-border bg-background/50 focus:border-primary/50 focus:ring-primary/10 placeholder:text-muted-foreground/60 w-full resize-none rounded-xl border-2 px-4 py-4 text-sm backdrop-blur-sm transition-all duration-200 focus:ring-4 focus:outline-none"
      //           autoFocus
      //         />
      //       </div>
      //     </div>
      //   );

      case "radio":
        return (
          <div className="flex w-full flex-col items-center space-y-2">
            {currentQuestion.metric && (
              <div className="from-primary/8 to-primary/12 border-primary/25 inline-flex items-center gap-2 rounded-full border bg-gradient-to-r px-3 py-1.5 shadow-sm">
                <Sparkle className="text-primary h-3 w-3" />
                <span className="text-primary text-xs font-medium">{currentQuestion.metric}</span>
              </div>
            )}

            <div className="space-y-3 text-center">
              <h3 className="text-foreground text-lg leading-tight font-semibold">
                {currentQuestion.question}
              </h3>
            </div>
            <div className="mt-2 w-full max-w-md space-y-3">
              {currentQuestion.options?.map((option, optionIndex) => {
                const isSelected = Array.isArray(currentValue)
                  ? currentValue.includes(option)
                  : currentValue === option;

                return (
                  <label
                    key={optionIndex}
                    className={`hover:bg-muted/50 relative flex cursor-pointer items-center rounded-lg border-2 p-4 transition-all duration-200 ${
                      isSelected
                        ? "border-primary bg-primary/5 shadow-md"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                  >
                    <input
                      type="checkbox"
                      name="current-question"
                      value={option}
                      checked={isSelected}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleAnswerChange(e.target.value)
                      }
                      className="sr-only"
                    />
                    <div className="flex w-full items-center space-x-3">
                      <div
                        className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
                          isSelected ? "border-primary bg-primary" : "border-muted-foreground/30"
                        }`}
                      >
                        {isSelected && <Check className="text-primary-foreground h-3 w-3" />}
                      </div>
                      <span className="text-foreground flex-1 text-sm font-medium">{option}</span>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-muted-foreground text-xs">{t`Select all that apply`}</p>
          </div>
        );

      default:
        return null;
    }
  };

  if (questions.length === 0) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[95vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
        {/* Header */}
        <div className="bg-muted/30 relative border-b">
          <div className="px-4 pt-4">
            <div className="mb-4 flex items-center space-x-3">
              <div className="p-2">
                <Sparkle weight="duotone" className="text-primary h-8 w-8" />
              </div>
              <div>
                <DialogTitle className="text-foreground text-xl font-bold">
                  {t`Optimization Questions`}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground/80 max-w-md text-xs">
                  {t`Quick questions for tailored resume optimization. Skip any you prefer.`}
                </DialogDescription>
              </div>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="flex-1 overflow-y-auto px-8 py-3">
          <div className="flex min-h-[300px] items-center">{renderCurrentQuestion()}</div>
        </div>

        {/* Footer */}
        <div className="bg-background/50 border-t">
          {/* Progress Bar */}
          <div className="mt-2 -mb-2 space-y-2">
            <div className="flex justify-center space-x-1">
              {questions.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-8 rounded-full border transition-all duration-300 ${
                    index === currentStep
                      ? "bg-primary border-primary scale-110"
                      : (() => {
                          const answer = answers[index];
                          if (Array.isArray(answer)) {
                            return answer.length > 0
                              ? "bg-primary/60 border-primary/60"
                              : "bg-muted-foreground/20 border-muted-foreground/30";
                          }
                          return answer && answer.trim() !== ""
                            ? "bg-primary/60 border-primary/60"
                            : "bg-muted-foreground/20 border-muted-foreground/30";
                        })()
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="space-y-4 px-8 py-6">
            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={isFirstStep}
                className="flex items-center gap-2 rounded-lg px-6 py-2.5"
              >
                <ArrowLeft className="h-4 w-4" />
                {t`Previous`}
              </Button>

              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={isLoading}
                  className="group bg-primary/5 hover:bg-primary/8 border-primary/20 hover:border-primary/35 text-primary hover:text-primary hover:shadow-primary/10 relative flex items-center gap-2 overflow-hidden rounded-lg border px-6 py-3 text-sm transition-all duration-300 hover:scale-[1.01] hover:shadow-md active:scale-[0.99]"
                >
                  <SkipForward className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  {isLoading ? (
                    <>
                      <CircleNotch className="h-4 w-4 animate-spin" />
                      <span className="font-medium">{t`Starting...`}</span>
                    </>
                  ) : (
                    <span className="relative z-10 font-medium">{t`Skip & start optimization`}</span>
                  )}
                </Button>

                {!isLastStep ? (
                  <Button
                    onClick={handleNext}
                    className="flex items-center gap-2 rounded-lg px-8 py-2.5"
                  >
                    {t`Next`}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-8 py-2.5"
                  >
                    {isLoading ? (
                      <>
                        <CircleNotch className="h-4 w-4 animate-spin" />
                        {t`Starting...`}
                      </>
                    ) : (
                      <>
                        <Sparkle className="h-4 w-4" />
                        {t`Start Optimization`}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
